import React, { useState } from 'react';

import { transformToCamelCase } from 'src/components/pageMeta/transformToCamelCase';
import { getLocalStorage } from 'src/utils/browserStorageHandler';
import { USER_TYPE } from 'src/utils/constants/localStorageConstants';
import { PARTNER_CONTENT_CONSTANTS } from 'src/utils/constants/stringConstants';
import {
  ASSET_TYPE,
  FeatureFlagConstants
} from 'src/utils/constants/utilConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';

import { useAppStore } from 'src/hooks/useAppStore';
import {
  ContentPartnerImage,
  ThumbnailImageProps,
  ThumbnailTypes
} from '../../types';
import * as customStyled from './styles';
import {
  checkForPremiumIcon,
  checkForZeeplexIcon,
  contentBillingType,
  contentDetailsList,
  getIsContentAccessible,
  partnerImageURLGenerator,
  premiumIconFlagBasedOnContent
} from './ThumbnailImageHandler';

const ThumbnailImage: React.FC<ThumbnailImageProps> = ({
  imageUrl,
  slug,
  assetType,
  title,
  businessType,
  billingType,
  onClickHandler,
  thumbnailType,
  contentPartner,
  content_partner_details,
  joystickIconCheck,
  children
}) => {
  const getImageAspectRatioFromThumbnailType = (): [number, number] => {
    switch (thumbnailType) {
      case ThumbnailTypes.vertical:
        return [2, 3];
      case ThumbnailTypes.circular:
        return [0, 1];
      case ThumbnailTypes.horizontal:
      case ThumbnailTypes.upnext:
      case ThumbnailTypes.plex:
        return [16, 9];
      case ThumbnailTypes.bellybanner:
        return [164, 39];
      default:
        return [2, 3];
    }
  };
  const billingTypeData = contentBillingType(businessType, billingType);

  const { initialLaunchData, remoteConfig, usersData } = useAppStore();
  const [isImgSrcFailed, setImgSrcFailed] = useState(false);
  const premiumTagFromInitialLaunch =
    initialLaunchData?.countrylist?.[0]?.menu_options?.premium_tag === 'yes'
      ? true
      : false;
  const userType = getLocalStorage(USER_TYPE);
  const packType = usersData?.packType;
  let isContentAccessible = false;
  if (packType) {
    isContentAccessible = getIsContentAccessible(packType);
  }
  const parterDetails = contentPartner
    ? transformToCamelCase(contentPartner, contentDetailsList)
    : content_partner_details;

  let partnerImgUrl;
  if (parterDetails) {
    partnerImgUrl = partnerImageURLGenerator(
      parterDetails,
      ContentPartnerImage.logoRectangleWhiteBG
    );
  }
  const shouldShowPremiumLogo =
    isFeatureEnabled(remoteConfig, FeatureFlagConstants.FEATURE_PREMIUM_LOGO) ||
    false;
  const shouldShowZeePlexLogo =
    isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.FEATURE_ZEE_PLEX_LOGO
    ) || false;
  const isPremiumContent = premiumIconFlagBasedOnContent(
    businessType,
    premiumTagFromInitialLaunch,
    shouldShowZeePlexLogo
  );
  const shouldShowZeePlexIcon = checkForZeeplexIcon(
    businessType,
    shouldShowZeePlexLogo
  );
  const shouldShowPremiumIcon =
    !parterDetails &&
    !joystickIconCheck &&
    checkForPremiumIcon(
      isContentAccessible,
      billingTypeData,
      isPremiumContent,
      shouldShowPremiumLogo,
      userType
    );

  const setFallbackImage: React.ReactEventHandler<HTMLImageElement> = (
    event
  ) => {
    const imgElement = event.currentTarget;
    imgElement.onerror = null;
    if (!isImgSrcFailed) {
      setImgSrcFailed(true);
      imgElement.src =
        thumbnailType === ThumbnailTypes.horizontal ||
        thumbnailType === ThumbnailTypes.upnext
          ? '/webapp-assets/images/fallback_landscape.svg'
          : '/webapp-assets/images/fallback_portrait.svg';
    }
  };

  return (
    <a
      data-testid="thumbnail-image"
      href={slug}
      onClick={onClickHandler}
      css={customStyled.removeDefaultStyle(thumbnailType)}>
      <figure
        css={customStyled.contentItemFigure(
          thumbnailType,
          ...getImageAspectRatioFromThumbnailType()
        )}>
        {children}
        {!isImgSrcFailed && parterDetails ? (
          <div>
            <img
              src={partnerImgUrl}
              css={customStyled.partnerLogoIcon}
              alt={PARTNER_CONTENT_CONSTANTS.PARTNER_LOGO}
            />
          </div>
        ) : null}

        <img
          src={imageUrl}
          loading="lazy"
          crossOrigin="anonymous"
          onError={setFallbackImage}
          alt={title}
        />

        {isImgSrcFailed && assetType === ASSET_TYPE.Movie ? (
          <div css={customStyled.imgErrorFallbackIcon}>
            <img
              width="100%"
              src="/webapp-assets/images/movies_icon.svg"
              alt={title}
              crossOrigin="anonymous"
            />
          </div>
        ) : null}

        {shouldShowZeePlexIcon ? (
          <div
            css={customStyled.cardTvodIconContainer(shouldShowZeePlexIcon)}
          />
        ) : shouldShowPremiumIcon ? (
          <div
            css={customStyled.cardPremiunIconContainer(shouldShowPremiumLogo)}
          />
        ) : null}
        {isImgSrcFailed ? (
          <div
            css={customStyled.imgErrorFallbackTitle(
              !!shouldShowPremiumIcon || !!shouldShowZeePlexIcon,
              !!shouldShowZeePlexIcon,
              thumbnailType
            )}>
            {title}
          </div>
        ) : null}
      </figure>
    </a>
  );
};

export default ThumbnailImage;
